import React, { useState, useMemo, useRef, useCallback } from 'react'
import { useEffect } from 'react'
import api, { type BsvDeviceTransferSession } from 'api/api'
import useStore from 'state/knovStore'
import styles from 'components/modals/wallet.module.scss'
import WalletSeeds from 'components/modals/WalletSeeds'
import WalletSwap from 'components/modals/WalletSwap'
import WalletDailyEarnings from 'components/modals/WalletDailyEarnings'
import WalletFileBackup from 'components/modals/WalletFileBackup'
import { recordWalletFileSaved } from 'lib/localWallet'
import WalletPasskeyRecovery from 'components/modals/WalletPasskeyRecovery'
import WalletSafetyCard, {
    WalletFileCheckPill,
    useLocalWalletStatus,
} from 'components/walletSetup/WalletSetupCard'
import WalletUsdCredits from 'components/modals/WalletUsdCredits'
import { toBitcoin, shortTxid } from 'lib/bsv-util'
import { copyTextToClipboard, fallbackCopyTextToClipboard } from 'lib/clipboard'
import cn from 'classnames'
import {
    DEVICE_TRANSFER_SESSION_EVENT,
    clearPendingDeviceTransferSession,
    getPendingDeviceTransferSessions,
    subscribePaymentUpdate,
    isTerminalPaymentStatus,
    isSuccessfulPaymentStatus,
    type DeviceTransferSessionEventDetail,
    type PaymentUpdate,
} from 'state/channels/useUserChannel'
import { toast } from 'utils/toast'
import { confirmBsvPaymentTray } from 'utils/bsvPaymentConfirmation'
import {
    convertBsvAmountInputUnit,
    getBsvAmountInputState,
    type BsvAmountUnit,
} from 'utils/bsvAmountInput'
import { BsvWalletType, PandaWallet, SHUAllet, initWallet } from 'wallets/wallets'
import useWalletDetails from 'api/useWalletDetails'
import useStripeAiBillingStatus, {
    refreshStripeAiBillingStatus,
} from 'api/useStripeAiBillingStatus'
import { useBsvExchangeRate, useSatsToDollars, formatUsdValue } from 'api/useBsvExchangeRate'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import OrdinalsStrayFunds from 'components/modals/OrdinalsStrayFunds'
import Tray from 'components/shared/Tray'
import CommonEditor from 'components/shared/CommonEditor'
import ReactQuill from 'react-quill-new'
import { getText } from 'lib/value'
import { usernameRegex } from 'components/auth/Signup'
import { isMobile } from 'lib/device'
import {
    clearClientShare,
    getClientShare,
    getLocalEncryptedWalletPayload,
    getLocalSelfCustodyPayloadStatus,
    getOrCreateLocalDeviceId,
    isSelfCustodyReinitializationRequiredError,
    type LocalSelfCustodyPayloadStatus,
    persistSelfCustodyWalletForDevice,
    putLocalEncryptedWalletPayloadRecord,
    recoverLocalWalletTextWithShares,
    storeClientShareForDevice,
    type ClientShareRecord,
} from 'lib/bsv-signing'
import { requestPersistentStorage } from 'lib/storage-persistence'
import { downloadWalletBackup } from 'lib/walletBackup'
import {
    cancelExternalCheckout,
    openExternalCheckout,
    prepareExternalCheckout,
} from 'utils/nativeApp'
import {
    decryptWalletPayload,
    deriveDeviceTransferSessionMaterial,
    computeDeviceTransferSasProof,
    encryptWalletPayload,
    generateDeviceTransferKeyPair,
    verifyDeviceTransferSasProof,
    type DeviceTransferKeyPair,
    type DeviceTransferRole,
    type DeviceTransferTranscript,
} from 'lib/bsv-device-transfer'
import {
    Wallet as WalletIcon,
    Send,
    Download,
    Copy,
    Check,
    ArrowUpRight,
    ArrowDownLeft,
    CreditCard,
    History,
    Activity,
    Lock,
    Loader2,
    ChevronDown,
    ExternalLink,
    // Unlock,
} from 'lucide-react'

const GUARDARIAN_STATUS_LABELS: Record<string, string> = {
    created: 'Created',
    new: 'Created',
    waiting: 'Waiting for payment...',
    pending: 'Payment received, processing...',
    exchange: 'Exchanging...',
    sending: 'Sending BSV to your wallet...',
    finished: 'Complete! BSV sent.',
    failed: 'Transaction failed.',
    expired: 'Transaction expired.',
    cancelled: 'Transaction cancelled.',
    refunded: 'Refunded.',
}

const GUARDARIAN_TERMINAL_STATUSES = new Set([
    'finished',
    'failed',
    'expired',
    'cancelled',
    'refunded',
])
const GUARDARIAN_IN_PROGRESS_STATUSES = new Set(['waiting', 'pending', 'exchange', 'sending'])
const GUARDARIAN_FAILED_STATUS = 'failed'
const GUARDARIAN_FAILED_VISIBLE_MS = 10 * 60 * 1000
const SHOW_BUY_HISTORY_UI = false

const AMOUNT_UNITS: BsvAmountUnit[] = ['bsv', 'sats']
const unitLabel = (unit: BsvAmountUnit) => (unit === 'bsv' ? 'BSV' : 'sats')

type SetupAction = 'generate' | 'register' | 'replace'

// Scope local consent to one accept attempt (session id + approver key), so a reset and
// re-accept of the same session never inherits an earlier confirmation.
function confirmationScope(session: {
    id: string
    approver_ephemeral_public_key: string | null
}): string {
    return `${session.id}:${session.approver_ephemeral_public_key || ''}`
}

function transcriptForSession(session: {
    id: string
    requesting_device_id: string
    approving_device_id: string | null
    requester_ephemeral_public_key: string
    approver_ephemeral_public_key: string | null
}): DeviceTransferTranscript {
    return {
        sessionId: session.id,
        requestingDeviceId: session.requesting_device_id,
        approvingDeviceId: session.approving_device_id || '',
        requesterEphemeralPublicKey: session.requester_ephemeral_public_key,
        approverEphemeralPublicKey: session.approver_ephemeral_public_key || '',
    }
}

const DEVICE_TRANSFER_TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'expired'])

const isTerminalDeviceTransferSession = (session: BsvDeviceTransferSession | null): boolean =>
    Boolean(session && DEVICE_TRANSFER_TERMINAL_STATUSES.has(session.status))

export default function Wallet({ closeModal, modalParams }) {
    const currentUserBsvWalletType = useStore(state => state.currentUserBsvWalletType)
    const userFeatures = useStore(state => state.userFeatures)
    const openDeviceTransferModal = useStore(
        state => state.actions.modalActions.openDeviceTransferModal,
    )
    const { data: walletDetailsData, isLoading: isInitialLoading } =
        useWalletDetails(currentUserBsvWalletType)

    // Treat balance as the critical readiness signal. Address can lag independently if address worker
    // is degraded, and we still want to render wallet/balance UI.
    const isLoading = isInitialLoading || !walletDetailsData?.balanceLoaded

    const hasBalanceLoaded = walletDetailsData?.balanceLoaded === true
    const currentUserBsvBalance = hasBalanceLoaded ? walletDetailsData?.balance || 0 : 0
    const currentUserBsvAddress = walletDetailsData?.address || ''
    const currentUserBsvOrdAddress = walletDetailsData?.ordinalsAddress || ''

    const wallet = walletDetailsData?.wallet

    const DEBUG = useStore(state => state.DEBUG)
    const hasBuyBsvFeature = false
    const bsvBalanceUsd = useSatsToDollars(currentUserBsvBalance)
    const bsvUsdRate = useBsvExchangeRate()
    const { useUsd, formatSatsAmount } = useBsvDisplay()
    const [amountInput, setAmountInput] = useState('')
    const [amountUnit, setAmountUnit] = useState<BsvAmountUnit>('bsv')
    // Derived, never stored: the sats we send are always re-read from the raw input under the
    // unit currently shown in the field, so the two can't drift apart.
    const amountState = useMemo(
        () => getBsvAmountInputState(amountInput, amountUnit),
        [amountInput, amountUnit],
    )
    const amountSats = amountState.amountSats
    const amountUsd = useSatsToDollars(
        typeof amountSats === 'number' && amountSats > 0 ? amountSats : null,
    )
    const [address, setAddress] = useState('')
    const [editorValue, setEditorValue] = useState(null)
    const [sending, setSending] = useState(false)
    const addressEditorRef = useRef(null)

    // Tracks the in-flight send: its payment-update subscription + safety timeout.
    const paymentTrackingRef = useRef<{ unsubscribe: () => void; timeout: number } | null>(null)
    // Intent-stable payment_id: reused while (recipient, amount) is unchanged so a natural
    // re-click after a swallowed network error hits the server's idempotency guard instead
    // of funding a second transaction.
    const paymentIntentRef = useRef<{ id: string; key: string } | null>(null)

    // to fund panda the funds need to be sent to what panda refers to as the wallet's `bsvAddress`, but
    // locks and other parts of the app that look at `currentUserBsvAddress` still need to use what panda
    // refers to as the wallets `identityAddress`, so we override `currentUserBsvAddress` in this case
    const [pandaDisplayAddress, setPandaDisplayAddress] = useState('')
    const [_isPandaAvailable] = useState(!!window.panda)
    const [isFragmenting, setIsFragmenting] = useState(false)
    const [settingUpUsdAiBilling, setSettingUpUsdAiBilling] = useState(false)
    const [activeTab, setActiveTab] = useState<'bsv' | 'stripe'>('bsv')
    const [stripeStatus, setStripeStatus] = useState<any>(null)
    const [stripeStatusLoading, setStripeStatusLoading] = useState(false)
    const stripeRefreshedOnOpenRef = useRef(false)
    const { data: cachedStripeStatus } = useStripeAiBillingStatus(false)
    const [copiedAddress, setCopiedAddress] = useState<'bsv' | 'ord' | null>(null)
    const [copiedSetupField, setCopiedSetupField] = useState<string | null>(null)
    const [clientShareRecord, setClientShareRecord] = useState<ClientShareRecord | null>(null)
    // Whether this device's client share has been looked up yet: until then a
    // missing record means "not read", not "this device has no keys".
    const [clientShareChecked, setClientShareChecked] = useState(false)
    const [clientShareStatusLoading, setClientShareStatusLoading] = useState(false)
    const [serverShareRegistered, setServerShareRegistered] = useState<boolean | null>(null)
    const [localWalletPayloadStatus, setLocalWalletPayloadStatus] =
        useState<LocalSelfCustodyPayloadStatus | null>(null)
    const [clientShareStatusError, setClientShareStatusError] = useState('')
    const [setupBusy, setSetupBusy] = useState(false)
    const [setupAction, setSetupAction] = useState<SetupAction | null>(null)
    const [setupError, setSetupError] = useState('')
    const [setupSuccess, setSetupSuccess] = useState('')
    const [localDeviceId, setLocalDeviceId] = useState('')
    const [knownDeviceTransferSessions, setKnownDeviceTransferSessions] = useState<
        BsvDeviceTransferSession[]
    >([])
    const [activeDeviceTransferId, setActiveDeviceTransferId] = useState<string | null>(null)
    const [activeDeviceTransferRole, setActiveDeviceTransferRole] =
        useState<DeviceTransferRole | null>(null)
    const [activeDeviceTransferKeyPair, setActiveDeviceTransferKeyPair] =
        useState<DeviceTransferKeyPair | null>(null)
    const [activeDeviceTransferSasNumbers, setActiveDeviceTransferSasNumbers] = useState<
        [number, number, number] | null
    >(null)
    const [activeDeviceTransferPayloadKey, setActiveDeviceTransferPayloadKey] =
        useState<CryptoKey | null>(null)
    const [activeDeviceTransferConfirmKey, setActiveDeviceTransferConfirmKey] =
        useState<CryptoKey | null>(null)
    // Session id whose numbers THIS device's user explicitly confirmed in this modal instance.
    // Never derived from server flags: the server (or anyone holding the login token) can set those.
    const [locallyConfirmedSessionId, setLocallyConfirmedSessionId] = useState<string | null>(null)
    const [deviceTransferBusy, setDeviceTransferBusy] = useState(false)
    const [deviceTransferError, setDeviceTransferError] = useState('')
    const [deviceTransferSuccess, setDeviceTransferSuccess] = useState('')
    const uploadedDeviceTransferIdsRef = useRef<Set<string>>(new Set())
    const restoredDeviceTransferIdsRef = useRef<Set<string>>(new Set())
    const activeDeviceTransferSessionRef = useRef<BsvDeviceTransferSession | null>(null)
    const activeDeviceTransferRoleRef = useRef<DeviceTransferRole | null>(null)
    const localDeviceIdRef = useRef('')
    const clientShareDeviceIdRef = useRef<string | null>(null)
    const walletModalMountedRef = useRef(true)

    const [buyAmount, setBuyAmount] = useState(20)
    const [buyCheckoutUrl, setBuyCheckoutUrl] = useState<string>('')
    const [buyPreparing, setBuyPreparing] = useState(false)
    const [buyError, setBuyError] = useState<string>('')
    const [, setBuyTxId] = useState<string>('')
    const [buyTrayOpen, setBuyTrayOpen] = useState(false)
    const [buyHistoryOpen, setBuyHistoryOpen] = useState(false)
    const [buyHistory, setBuyHistory] = useState<any[]>([])
    const [buyHistoryLoading, setBuyHistoryLoading] = useState(false)
    const pendingTxIdsRef = useRef<Set<string>>(new Set())
    const buyCreateInFlightRef = useRef(false)
    const [historyPolling, setHistoryPolling] = useState(false)
    const [statusNowMs, setStatusNowMs] = useState(Date.now())
    const currentUserId = gon?.currentUser?.id
    const hasClientSigningFeature = useMemo(
        () =>
            Boolean(
                userFeatures?.some(
                    (feature: any) =>
                        (typeof feature === 'string' ? feature : feature?.name) ===
                        'use_client_signing',
                ),
            ),
        [userFeatures],
    )
    const showDevClientSetup =
        hasClientSigningFeature &&
        currentUserBsvWalletType === BsvWalletType.SHUALLET &&
        Boolean(currentUserId)
    // Browser-wallet accounts (new signups) have no Treechat-held wallet to set a device up
    // from; their setup lives in WalletSetupHost.
    const {
        status: localWalletStatus,
        isLocal: isLocalWalletAccount,
        setStatus: setLocalWalletStatus,
    } = useLocalWalletStatus(showDevClientSetup)
    // A wallet file saved from here counts toward a browser-wallet account's setup.
    const recordLocalWalletFileSaved = useCallback(async () => {
        if (isLocalWalletAccount !== true) return
        try {
            setLocalWalletStatus(await recordWalletFileSaved())
        } catch (error) {
            console.error('Error recording the saved wallet file:', error)
        }
    }, [isLocalWalletAccount, setLocalWalletStatus])
    const activeDeviceTransferSession = useMemo(
        () =>
            knownDeviceTransferSessions.find(session => session.id === activeDeviceTransferId) ||
            null,
        [activeDeviceTransferId, knownDeviceTransferSessions],
    )
    const selfCustodyBrowserNeedsReinitialization =
        Boolean(clientShareRecord) &&
        (localWalletPayloadStatus === 'missing' || localWalletPayloadStatus === 'legacy')
    const canRegisterExistingDevice =
        Boolean(clientShareRecord) &&
        serverShareRegistered === false &&
        !selfCustodyBrowserNeedsReinitialization
    const showReplaceSetupAction = Boolean(clientShareRecord)
    const selfCustodySetupHint = useMemo(() => {
        if (!clientShareRecord) {
            return 'This browser can either bootstrap itself from the existing wallet backup or request an encrypted handoff from another trusted device.'
        }

        if (selfCustodyBrowserNeedsReinitialization) {
            return 'This browser still has a share record, but its local full-wallet package is missing or outdated. Re-set up self-custody on this browser to repair it.'
        }

        if (serverShareRegistered === false) {
            return 'This browser already has a local client share, but the matching server share is missing. Use Register This Device to repair it without touching the console.'
        }

        if (serverShareRegistered) {
            return 'This browser is already configured for client signing. You can refresh its status or replace the current share pair while keeping the same device id.'
        }

        return 'Refresh status to confirm whether this browser still has a matching server share registered.'
    }, [clientShareRecord, selfCustodyBrowserNeedsReinitialization, serverShareRegistered])
    const canRequestTransferFromAnotherDevice = showDevClientSetup && Boolean(localDeviceId)
    const incomingDeviceTransferSessions = useMemo(() => {
        if (!clientShareRecord?.deviceId) {
            return []
        }

        return knownDeviceTransferSessions.filter(session => {
            if (session.id === activeDeviceTransferId) {
                return false
            }

            if (session.requesting_device_id === clientShareRecord.deviceId) {
                return false
            }

            return session.status === 'pending'
        })
    }, [activeDeviceTransferId, clientShareRecord?.deviceId, knownDeviceTransferSessions])
    const localDeviceTransferConfirmed = useMemo(() => {
        if (!activeDeviceTransferSession || !activeDeviceTransferRole) {
            return false
        }

        if (activeDeviceTransferRole === 'requester') {
            return activeDeviceTransferSession.requester_sas_confirmed
        }

        return (
            activeDeviceTransferSession.approver_sas_confirmed &&
            locallyConfirmedSessionId === confirmationScope(activeDeviceTransferSession)
        )
    }, [activeDeviceTransferRole, activeDeviceTransferSession, locallyConfirmedSessionId])

    const onChange = useCallback(value => {
        setEditorValue(value)
        if (addressEditorRef.current) {
            const editor = addressEditorRef.current.getEditor()
            if (editor) {
                const text = getText(editor.getContents())?.trim()
                setAddress(text)
            }
        }
    }, [])

    useEffect(() => {
        async function walletInstantiator() {
            const wallet = initWallet(currentUserBsvWalletType)
            await wallet?.authenticate?.()
            if (wallet?.type() === BsvWalletType.PANDA) {
                setPandaDisplayAddress((await window.panda?.getAddresses())?.bsvAddress)
            }
        }
        walletInstantiator()
    }, [currentUserBsvWalletType])

    useEffect(() => {
        walletModalMountedRef.current = true

        return () => {
            walletModalMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        // Clean up checkout return params from URL if present.
        try {
            const url = new URL(window.location.href)
            if (url.searchParams.has('wallet_notice_type')) {
                url.searchParams.delete('wallet_notice_type')
                url.searchParams.delete('wallet_notice')
                url.searchParams.delete('open_wallet')
                window.history.replaceState({}, '', url.toString())
            }
        } catch (_) {}
    }, [])

    // Guardarian minimum is ~€15 (~$17). We default to $20 to stay above it.
    // Their API doesn't expose a min-amount endpoint; the minimum is enforced at checkout.

    // Load recent exchange transactions on mount, sync stale ones with Guardarian,
    // and seed history + active-tx state.
    useEffect(() => {
        if (!hasBuyBsvFeature) return

        let cancelled = false
        const syncRecentTransactions = async () => {
            if (cancelled) return
            const txs = await api.guardarianListTransactions(50)
            if (cancelled || !Array.isArray(txs) || txs.length === 0) return

            // Immediately sync any non-terminal transactions with Guardarian.
            const stale = txs.filter(
                t => t?.external_id && !GUARDARIAN_TERMINAL_STATUSES.has(t.status),
            )

            const synced = await Promise.all(
                stale.map(async tx => {
                    try {
                        const res = await api.guardarianGetTransaction(tx.external_id)
                        return { id: tx.external_id, status: res?.status || tx.status }
                    } catch (_) {
                        return { id: tx.external_id, status: tx.status }
                    }
                }),
            )
            if (cancelled) return

            // Merge synced statuses back into the list.
            const statusMap = Object.fromEntries(synced.map(s => [s.id, s.status]))
            const updatedTxs = txs.map(t =>
                statusMap[t.external_id] != null ? { ...t, status: statusMap[t.external_id] } : t,
            )

            setBuyHistory(updatedTxs)

            // Collect still-pending IDs after sync.
            const pendingIds = updatedTxs
                .filter(t => t?.external_id && !GUARDARIAN_TERMINAL_STATUSES.has(t.status))
                .map(t => t.external_id)
            pendingTxIdsRef.current = new Set(pendingIds)

            // Pick the latest non-terminal transaction for the status box.
            const active = updatedTxs.find(
                t => t?.external_id && !GUARDARIAN_TERMINAL_STATUSES.has(t.status),
            )
            if (active) {
                setBuyTxId(active.external_id)
                setBuyCheckoutUrl(active.checkout_url || '')
            } else {
                setBuyTxId('')
                setBuyCheckoutUrl('')
            }

            if (pendingIds.length > 0) setHistoryPolling(true)

            // Refresh wallet balance if any transaction just resolved as finished.
            if (synced.some(s => s.status === 'finished')) {
                try {
                    const w = initWallet(currentUserBsvWalletType)
                    w?.getBalance()
                } catch (_) {}
            }
        }

        ;(async () => {
            for (let attempt = 1; attempt <= 2; attempt += 1) {
                try {
                    await syncRecentTransactions()
                    return
                } catch (err) {
                    if (cancelled) return
                    console.warn(`Guardarian bootstrap sync failed (attempt ${attempt}/2):`, err)
                    if (attempt === 1) {
                        await new Promise(resolve => setTimeout(resolve, 1500))
                    }
                }
            }
        })()
        return () => {
            cancelled = true
        }
    }, [hasBuyBsvFeature])

    // Poll all non-terminal Guardarian transactions until they resolve.
    useEffect(() => {
        if (!historyPolling || pendingTxIdsRef.current.size === 0) return

        let cancelled = false

        const poll = async () => {
            const ids = Array.from(pendingTxIdsRef.current)
            if (ids.length === 0) {
                setHistoryPolling(false)
                return
            }

            let anyFinished = false
            const updates: Record<string, any> = {}

            await Promise.all(
                ids.map(async externalId => {
                    try {
                        const res = await api.guardarianGetTransaction(externalId)
                        if (cancelled) return
                        const status = res?.status || 'unknown'
                        updates[externalId] = res
                        if (GUARDARIAN_TERMINAL_STATUSES.has(status)) {
                            pendingTxIdsRef.current.delete(externalId)
                        }
                        if (status === 'finished') anyFinished = true
                    } catch (err) {
                        console.warn('Guardarian poll error:', err)
                    }
                }),
            )

            if (cancelled) return

            // Apply status updates to buyHistory.
            if (Object.keys(updates).length > 0) {
                setBuyHistory(prev =>
                    prev.map(t =>
                        updates[t.external_id] != null
                            ? {
                                  ...t,
                                  ...updates[t.external_id],
                                  status: updates[t.external_id].status,
                              }
                            : t,
                    ),
                )
            }

            if (anyFinished) {
                try {
                    const w = initWallet(currentUserBsvWalletType)
                    w?.getBalance()
                } catch (_) {}
            }

            // Stop polling once all are resolved.
            if (pendingTxIdsRef.current.size === 0) {
                setHistoryPolling(false)
                setBuyTxId('')
                setBuyCheckoutUrl('')
            }
        }

        poll()
        const interval = setInterval(poll, 10_000)
        return () => {
            cancelled = true
            clearInterval(interval)
        }
    }, [historyPolling, currentUserBsvWalletType])

    // Clean up any in-flight payment tracking when the modal unmounts.
    useEffect(() => {
        return () => {
            if (paymentTrackingRef.current) {
                paymentTrackingRef.current.unsubscribe()
                window.clearTimeout(paymentTrackingRef.current.timeout)
                paymentTrackingRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        if (cachedStripeStatus) setStripeStatus(cachedStripeStatus)
    }, [cachedStripeStatus])

    useEffect(() => {
        const shouldRefreshFromStripe = !stripeRefreshedOnOpenRef.current
        if (shouldRefreshFromStripe) stripeRefreshedOnOpenRef.current = true
        if (!shouldRefreshFromStripe && activeTab !== 'stripe') return

        let cancelled = false
        ;(async () => {
            try {
                setStripeStatusLoading(true)
                const priorRefreshedAt = stripeStatus?.stripe?.refreshed_at || null
                const res = await refreshStripeAiBillingStatus(shouldRefreshFromStripe)
                if (!cancelled) setStripeStatus(res)

                if (shouldRefreshFromStripe) {
                    for (let attempt = 1; attempt <= 5 && !cancelled; attempt += 1) {
                        const refreshedAtRaw = res?.stripe?.refreshed_at || null
                        if (refreshedAtRaw && refreshedAtRaw !== priorRefreshedAt) break

                        await new Promise(resolve => setTimeout(resolve, 1500))
                        const polled = await refreshStripeAiBillingStatus(false)
                        if (!cancelled) setStripeStatus(polled)
                        const polledRefreshedAtRaw = polled?.stripe?.refreshed_at || null
                        if (polledRefreshedAtRaw && polledRefreshedAtRaw !== priorRefreshedAt) break
                    }
                }
            } catch (e) {
                if (!cancelled) setStripeStatus({ error: 'Failed to load Stripe status' })
            } finally {
                if (!cancelled) setStripeStatusLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [activeTab])

    const handleCopy = async (addr: string, type: 'bsv' | 'ord') => {
        if (!addr) return

        const done = () => {
            setCopiedAddress(type)
            setTimeout(() => setCopiedAddress(null), 2000)
        }

        if (await copyTextToClipboard(addr)) {
            done()
            return
        }

        alert('Failed to copy to clipboard')
    }

    // Some browsers require clipboard writes to be fully synchronous when also opening a new tab/window.
    // This helper uses the synchronous fallback copy path and updates the same "copied" UI state.
    const handleCopySync = (text: string, type: 'bsv' | 'ord') => {
        if (!text) return false
        const ok = fallbackCopyTextToClipboard(text)
        if (ok) {
            setCopiedAddress(type)
            setTimeout(() => setCopiedAddress(null), 2000)
        }
        return ok
    }

    const handleCopySetupField = async (text: string, field: string) => {
        if (!text) return

        const done = () => {
            setCopiedSetupField(field)
            setTimeout(() => setCopiedSetupField(null), 2000)
        }

        if (await copyTextToClipboard(text)) {
            done()
            return
        }

        alert('Failed to copy to clipboard')
    }

    const upsertDeviceTransferSession = useCallback((session: BsvDeviceTransferSession) => {
        setKnownDeviceTransferSessions(previousSessions => {
            const remainingSessions = previousSessions.filter(
                previousSession => previousSession.id !== session.id,
            )

            if (DEVICE_TRANSFER_TERMINAL_STATUSES.has(session.status)) {
                return remainingSessions
            }

            return [...remainingSessions, session]
        })
    }, [])

    const clearActiveDeviceTransfer = useCallback((clearSuccess = false) => {
        setActiveDeviceTransferId(null)
        setActiveDeviceTransferRole(null)
        setActiveDeviceTransferKeyPair(null)
        setActiveDeviceTransferSasNumbers(null)
        setActiveDeviceTransferPayloadKey(null)
        setActiveDeviceTransferConfirmKey(null)
        setLocallyConfirmedSessionId(null)
        setDeviceTransferBusy(false)
        setDeviceTransferError('')
        if (clearSuccess) {
            setDeviceTransferSuccess('')
        }
    }, [])

    const refreshClientShareStatus = useCallback(async () => {
        if (!showDevClientSetup || !currentUserId) return

        setClientShareStatusLoading(true)
        setClientShareStatusError('')

        try {
            const record = await getClientShare(currentUserId)
            setClientShareRecord(record)
            setClientShareChecked(true)
            const existingLocalDeviceId =
                record?.deviceId || (await getOrCreateLocalDeviceId(currentUserId))
            setLocalDeviceId(existingLocalDeviceId)

            if (!record) {
                setServerShareRegistered(null)
                setLocalWalletPayloadStatus(null)
                return
            }

            const payloadStatus = await getLocalSelfCustodyPayloadStatus(currentUserId)
            setLocalWalletPayloadStatus(payloadStatus)
            const serverShare = await api.getBsvServerShare(currentUserId, record.deviceId)
            setServerShareRegistered(Boolean(serverShare))
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            setClientShareStatusError(errorMessage)
        } finally {
            setClientShareStatusLoading(false)
        }
    }, [currentUserId, showDevClientSetup])

    useEffect(() => {
        if (showDevClientSetup) {
            void requestPersistentStorage()
        }
        refreshClientShareStatus()
    }, [refreshClientShareStatus, showDevClientSetup])

    const handleGenerateClientSetup = useCallback(
        async (action: SetupAction) => {
            if (!currentUserId || !localDeviceId) return

            if (action === 'replace' && clientShareRecord) {
                const shouldReplace = window.confirm(
                    'Replace this browser setup? This will refresh the client/server share pair while keeping the same device id.',
                )
                if (!shouldReplace) return
            }

            setSetupBusy(true)
            setSetupAction(action)
            setSetupError('')
            setSetupSuccess('')

            const previousRecord = clientShareRecord
            const previousWalletPayload = currentUserId
                ? await getLocalEncryptedWalletPayload(currentUserId)
                : null
            let storedRecord: ClientShareRecord | null = null
            try {
                const walletBlob = await api.downloadBsvWallet()
                const walletText = await walletBlob.text()
                const persistedWallet = await persistSelfCustodyWalletForDevice(
                    currentUserId,
                    localDeviceId,
                    walletText,
                )
                storedRecord = persistedWallet.clientShareRecord
                const registerResult = await api.registerBsvServerShare(
                    storedRecord.deviceId,
                    persistedWallet.shares.treechatShare,
                    persistedWallet.walletIdentity,
                )

                if (!registerResult.success) {
                    throw new Error(registerResult.error || 'Failed to register server share')
                }

                if (
                    registerResult.address &&
                    registerResult.address !== persistedWallet.walletIdentity.paymentAddress
                ) {
                    throw new Error(
                        'Server-derived address did not match the recovered wallet payment address',
                    )
                }

                await refreshClientShareStatus()
                let successMessage = 'This browser is now ready to sign upvalues.'
                if (action === 'register') {
                    successMessage =
                        'This browser was repaired and its matching server share is now registered.'
                } else if (action === 'replace') {
                    successMessage =
                        'This browser setup was replaced and remains registered under the same device id.'
                }
                setSetupSuccess(successMessage)
                window.alert(
                    `${successMessage} Treechat will refresh once so this tab starts listening for signing requests.`,
                )
                window.location.reload()
            } catch (error) {
                try {
                    if (previousRecord) {
                        await storeClientShareForDevice(
                            currentUserId,
                            previousRecord.deviceId,
                            previousRecord.clientShare,
                            previousRecord.address,
                        )
                    } else if (storedRecord) {
                        await clearClientShare(currentUserId)
                    }

                    if (previousWalletPayload) {
                        await putLocalEncryptedWalletPayloadRecord(previousWalletPayload)
                    }
                } catch (rollbackError) {
                    console.error('Failed to roll back client share setup:', rollbackError)
                }

                const errorMessage = error instanceof Error ? error.message : String(error)
                setSetupError(errorMessage)
                await refreshClientShareStatus()
            } finally {
                setSetupBusy(false)
                setSetupAction(null)
            }
        },
        [clientShareRecord, currentUserId, localDeviceId, refreshClientShareStatus],
    )

    const handleDownloadWallet = useCallback(
        async (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault()

            try {
                await downloadWalletBackup(
                    hasClientSigningFeature && currentUserId ? 'client_shares' : 'server',
                    currentUserId,
                )
                await recordLocalWalletFileSaved()
            } catch (error) {
                console.error('Error downloading wallet:', error)
                alert(
                    error instanceof Error ? error.message : 'Wallet not found or error occurred.',
                )
            }
        },
        [currentUserId, hasClientSigningFeature, recordLocalWalletFileSaved],
    )

    useEffect(() => {
        setKnownDeviceTransferSessions(getPendingDeviceTransferSessions())
    }, [])

    useEffect(() => {
        if (!showDevClientSetup) {
            return
        }

        const handleDeviceTransferEvent = (event: Event) => {
            const detail = (event as CustomEvent<DeviceTransferSessionEventDetail>).detail
            const deviceTransferSession = detail.deviceTransferSession
            upsertDeviceTransferSession(deviceTransferSession)

            if (
                detail.eventType === 'accepted' &&
                activeDeviceTransferRole === 'approver' &&
                activeDeviceTransferId === deviceTransferSession.id &&
                clientShareRecord?.deviceId &&
                deviceTransferSession.approving_device_id !== clientShareRecord.deviceId
            ) {
                clearActiveDeviceTransfer()
                setDeviceTransferError(
                    'Another trusted device accepted this request first. This browser has dismissed its local transfer UI.',
                )
            }

            if (
                detail.eventType === 'released' &&
                activeDeviceTransferRole === 'approver' &&
                activeDeviceTransferId === deviceTransferSession.id
            ) {
                clearActiveDeviceTransfer()
                setDeviceTransferSuccess(
                    'This transfer request was released. The requesting device is now waiting for another trusted device to accept it.',
                )
            }

            if (
                detail.eventType === 'cancelled' &&
                activeDeviceTransferId === deviceTransferSession.id
            ) {
                clearActiveDeviceTransfer()
                setDeviceTransferError('This transfer request was cancelled before completion.')
            }

            if (
                detail.eventType === 'completed' &&
                activeDeviceTransferId === deviceTransferSession.id
            ) {
                clearActiveDeviceTransfer()
                if (activeDeviceTransferRole === 'approver') {
                    setDeviceTransferSuccess(
                        'The new device finished restoring and activated its own share pair. This browser no longer needs to keep the transfer UI open.',
                    )
                }
            }
        }

        window.addEventListener(DEVICE_TRANSFER_SESSION_EVENT, handleDeviceTransferEvent)
        return () => {
            window.removeEventListener(DEVICE_TRANSFER_SESSION_EVENT, handleDeviceTransferEvent)
        }
    }, [
        activeDeviceTransferId,
        activeDeviceTransferRole,
        clearActiveDeviceTransfer,
        clientShareRecord?.deviceId,
        showDevClientSetup,
        upsertDeviceTransferSession,
    ])

    useEffect(() => {
        if (!activeDeviceTransferId) {
            return
        }

        if (!activeDeviceTransferSession) {
            clearPendingDeviceTransferSession(activeDeviceTransferId)
            clearActiveDeviceTransfer()
            return
        }

        if (isTerminalDeviceTransferSession(activeDeviceTransferSession)) {
            clearPendingDeviceTransferSession(activeDeviceTransferId)
            if (activeDeviceTransferSession?.status === 'completed') {
                return
            }

            clearActiveDeviceTransfer()
            return
        }

        let cancelled = false
        const intervalId = window.setInterval(async () => {
            const result = await api.getBsvDeviceTransferSession(activeDeviceTransferId)
            if (cancelled) {
                return
            }

            if (!result.session) {
                if (result.error) {
                    clearActiveDeviceTransfer()
                    setDeviceTransferError(result.error)
                }
                return
            }

            upsertDeviceTransferSession(result.session)
        }, 5000)

        return () => {
            cancelled = true
            window.clearInterval(intervalId)
        }
    }, [
        activeDeviceTransferId,
        activeDeviceTransferSession,
        clearActiveDeviceTransfer,
        upsertDeviceTransferSession,
    ])

    useEffect(() => {
        if (!activeDeviceTransferSession || !activeDeviceTransferKeyPair) {
            setActiveDeviceTransferSasNumbers(null)
            setActiveDeviceTransferPayloadKey(null)
            setActiveDeviceTransferConfirmKey(null)
            return
        }

        if (
            !activeDeviceTransferSession.approving_device_id ||
            !activeDeviceTransferSession.approver_ephemeral_public_key
        ) {
            setActiveDeviceTransferSasNumbers(null)
            setActiveDeviceTransferPayloadKey(null)
            setActiveDeviceTransferConfirmKey(null)
            return
        }

        const remotePublicKey =
            activeDeviceTransferRole === 'approver'
                ? activeDeviceTransferSession.requester_ephemeral_public_key
                : activeDeviceTransferSession.approver_ephemeral_public_key

        let cancelled = false
        ;(async () => {
            try {
                const sessionMaterial = await deriveDeviceTransferSessionMaterial(
                    activeDeviceTransferKeyPair.privateKey,
                    remotePublicKey,
                    transcriptForSession(activeDeviceTransferSession),
                )

                if (cancelled) {
                    return
                }

                setActiveDeviceTransferSasNumbers(sessionMaterial.sasNumbers)
                setActiveDeviceTransferPayloadKey(sessionMaterial.payloadKey)
                setActiveDeviceTransferConfirmKey(sessionMaterial.confirmKey)
            } catch (error) {
                if (cancelled) {
                    return
                }

                setDeviceTransferError(
                    error instanceof Error ? error.message : 'Failed to derive SAS numbers',
                )
            }
        })()

        return () => {
            cancelled = true
        }
    }, [activeDeviceTransferKeyPair, activeDeviceTransferRole, activeDeviceTransferSession])

    useEffect(() => {
        if (
            activeDeviceTransferRole !== 'approver' ||
            !activeDeviceTransferSession ||
            !activeDeviceTransferPayloadKey ||
            !activeDeviceTransferConfirmKey ||
            !activeDeviceTransferSasNumbers ||
            locallyConfirmedSessionId !== confirmationScope(activeDeviceTransferSession) ||
            !activeDeviceTransferSession.requester_sas_proof ||
            activeDeviceTransferSession.payload_ready ||
            uploadedDeviceTransferIdsRef.current.has(activeDeviceTransferSession.id)
        ) {
            return
        }

        uploadedDeviceTransferIdsRef.current.add(activeDeviceTransferSession.id)
        let cancelled = false

        ;(async () => {
            let shouldAllowRetry = true
            try {
                setDeviceTransferBusy(true)
                setDeviceTransferError('')

                // Require a requester confirmation that only the holder of the requesting
                // device's ephemeral key could have produced; server flags are not trusted.
                const requesterProofValid = await verifyDeviceTransferSasProof(
                    activeDeviceTransferConfirmKey,
                    'requester',
                    activeDeviceTransferSasNumbers,
                    transcriptForSession(activeDeviceTransferSession),
                    activeDeviceTransferSession.requester_sas_proof,
                )
                if (!requesterProofValid) {
                    shouldAllowRetry = false
                    throw new Error(
                        'The new device could not prove it saw the same numbers. Cancel and start the transfer again.',
                    )
                }

                const approvingDeviceId = clientShareRecord?.deviceId || ''
                if (!currentUserId || !approvingDeviceId || !clientShareRecord?.clientShare) {
                    shouldAllowRetry = false
                    throw new Error(
                        'This device is missing its local self-custody share state. Re-set up self-custody on this browser before transferring again.',
                    )
                }

                const serverShare = await api.getBsvServerShare(currentUserId, approvingDeviceId)
                if (!serverShare?.treechatShare) {
                    shouldAllowRetry = false
                    throw new Error(
                        'This device is missing its matching server share. Re-set up self-custody on this browser before transferring again.',
                    )
                }

                const walletText = await recoverLocalWalletTextWithShares(
                    currentUserId,
                    serverShare.treechatShare,
                    clientShareRecord.clientShare,
                )
                const envelope = await encryptWalletPayload(
                    walletText,
                    activeDeviceTransferPayloadKey,
                )
                const uploadResult = await api.uploadBsvDeviceTransferPayload(
                    activeDeviceTransferSession.id,
                    clientShareRecord?.deviceId || '',
                    envelope.encryptedWalletPayload,
                    envelope.encryptedWalletNonce,
                )

                if (uploadResult.session) {
                    upsertDeviceTransferSession(uploadResult.session)
                }

                if (uploadResult.error) {
                    throw new Error(uploadResult.error)
                }

                if (cancelled || !uploadResult.session) {
                    return
                }

                setDeviceTransferSuccess(
                    'The encrypted wallet has been uploaded. Waiting for the new device to restore and activate itself.',
                )
            } catch (error) {
                if (isSelfCustodyReinitializationRequiredError(error)) {
                    shouldAllowRetry = false
                }
                if (shouldAllowRetry) {
                    uploadedDeviceTransferIdsRef.current.delete(activeDeviceTransferSession.id)
                }
                if (cancelled) {
                    return
                }

                setDeviceTransferError(
                    error instanceof Error
                        ? error.message
                        : 'Failed to upload encrypted wallet payload',
                )
            } finally {
                if (!cancelled) {
                    setDeviceTransferBusy(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        activeDeviceTransferConfirmKey,
        activeDeviceTransferPayloadKey,
        activeDeviceTransferRole,
        activeDeviceTransferSasNumbers,
        activeDeviceTransferSession,
        clientShareRecord?.clientShare,
        clientShareRecord?.deviceId,
        currentUserId,
        locallyConfirmedSessionId,
        upsertDeviceTransferSession,
    ])

    useEffect(() => {
        if (
            activeDeviceTransferRole !== 'requester' ||
            !activeDeviceTransferSession ||
            !activeDeviceTransferPayloadKey ||
            !activeDeviceTransferSession.payload_ready ||
            restoredDeviceTransferIdsRef.current.has(activeDeviceTransferSession.id)
        ) {
            return
        }

        restoredDeviceTransferIdsRef.current.add(activeDeviceTransferSession.id)
        let cancelled = false

        ;(async () => {
            try {
                setDeviceTransferBusy(true)
                setDeviceTransferError('')
                const payloadResult = await api.getBsvDeviceTransferPayload(
                    activeDeviceTransferSession.id,
                    localDeviceId,
                )

                if (!payloadResult.payload) {
                    throw new Error(
                        payloadResult.error || 'Failed to download encrypted wallet payload',
                    )
                }

                const walletText = await decryptWalletPayload(
                    payloadResult.payload.encrypted_wallet_payload,
                    payloadResult.payload.encrypted_wallet_nonce,
                    activeDeviceTransferPayloadKey,
                )
                if (!currentUserId) {
                    throw new Error('Missing current user id for wallet restore')
                }
                const persistedWallet = await persistSelfCustodyWalletForDevice(
                    currentUserId,
                    localDeviceId,
                    walletText,
                )

                const registerResult = await api.registerBsvServerShare(
                    localDeviceId,
                    persistedWallet.shares.treechatShare,
                    persistedWallet.walletIdentity,
                )

                if (!registerResult.success) {
                    throw new Error(registerResult.error || 'Failed to register server share')
                }

                const activateResult = await api.activateBsvDeviceTransferSession(
                    activeDeviceTransferSession.id,
                    localDeviceId,
                )

                clearPendingDeviceTransferSession(activeDeviceTransferSession.id)
                if (activateResult.error || !activateResult.session) {
                    throw new Error(activateResult.error)
                }

                if (!walletModalMountedRef.current) {
                    return
                }

                await refreshClientShareStatus()
                clearActiveDeviceTransfer()
                setDeviceTransferSuccess(
                    'This browser now has its own local signing shares. Treechat will refresh so it starts listening for signing requests.',
                )
                window.alert(
                    'This browser has restored the wallet and registered its own share pair. Treechat will refresh once so this tab starts listening for signing requests.',
                )
                window.location.reload()
            } catch (error) {
                restoredDeviceTransferIdsRef.current.delete(activeDeviceTransferSession.id)
                if (currentUserId) {
                    await clearClientShare(currentUserId)
                }

                if (cancelled) {
                    return
                }

                setDeviceTransferError(
                    error instanceof Error
                        ? error.message
                        : 'Failed to restore encrypted wallet payload',
                )
            } finally {
                if (!cancelled) {
                    setDeviceTransferBusy(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        activeDeviceTransferPayloadKey,
        activeDeviceTransferRole,
        activeDeviceTransferSession,
        currentUserId,
        localDeviceId,
        clearActiveDeviceTransfer,
        refreshClientShareStatus,
    ])

    useEffect(() => {
        activeDeviceTransferSessionRef.current = activeDeviceTransferSession
        activeDeviceTransferRoleRef.current = activeDeviceTransferRole
    }, [activeDeviceTransferRole, activeDeviceTransferSession])

    useEffect(() => {
        localDeviceIdRef.current = localDeviceId
        clientShareDeviceIdRef.current = clientShareRecord?.deviceId || null
    }, [clientShareRecord?.deviceId, localDeviceId])

    useEffect(() => {
        return () => {
            const session = activeDeviceTransferSessionRef.current
            const role = activeDeviceTransferRoleRef.current

            if (!session || !role || isTerminalDeviceTransferSession(session)) {
                return
            }

            const deviceId =
                role === 'requester' ? localDeviceIdRef.current : clientShareDeviceIdRef.current
            if (!deviceId) {
                return
            }

            if (role === 'approver') {
                api.releaseBsvDeviceTransferSession(session.id, deviceId)
                return
            }

            api.cancelBsvDeviceTransferSession(session.id, deviceId)
        }
    }, [])

    const handleRequestDeviceTransfer = useCallback(async () => {
        if (!currentUserId || !localDeviceId) {
            return
        }

        try {
            setDeviceTransferBusy(true)
            setDeviceTransferError('')
            setDeviceTransferSuccess('Waiting for another trusted device to accept this request.')

            const keyPair = await generateDeviceTransferKeyPair()
            const createResult = await api.createBsvDeviceTransferSession(
                localDeviceId,
                keyPair.publicKeyBase64Url,
            )

            if (createResult.error || !createResult.session) {
                throw new Error(createResult.error || 'Failed to create device transfer session')
            }

            setActiveDeviceTransferId(createResult.session.id)
            setActiveDeviceTransferRole('requester')
            setActiveDeviceTransferKeyPair(keyPair)
            upsertDeviceTransferSession(createResult.session)
        } catch (error) {
            setDeviceTransferError(
                error instanceof Error ? error.message : 'Failed to request wallet transfer',
            )
            setDeviceTransferSuccess('')
        } finally {
            setDeviceTransferBusy(false)
        }
    }, [currentUserId, localDeviceId, upsertDeviceTransferSession])

    const handleAcceptDeviceTransfer = useCallback(
        async (session: BsvDeviceTransferSession) => {
            if (!clientShareRecord?.deviceId) {
                return
            }

            try {
                setDeviceTransferBusy(true)
                setDeviceTransferError('')
                setDeviceTransferSuccess('')

                const keyPair = await generateDeviceTransferKeyPair()
                const acceptResult = await api.acceptBsvDeviceTransferSession(
                    session.id,
                    clientShareRecord.deviceId,
                    keyPair.publicKeyBase64Url,
                )

                if (acceptResult.session) {
                    upsertDeviceTransferSession(acceptResult.session)
                }

                if (acceptResult.error || !acceptResult.session) {
                    throw new Error(acceptResult.error || 'Failed to accept device transfer')
                }

                setActiveDeviceTransferId(acceptResult.session.id)
                setActiveDeviceTransferRole('approver')
                setActiveDeviceTransferKeyPair(keyPair)
                setDeviceTransferSuccess(
                    'Compare the three numbers on both devices, then confirm that they match.',
                )
            } catch (error) {
                setDeviceTransferError(
                    error instanceof Error ? error.message : 'Failed to accept device transfer',
                )
            } finally {
                setDeviceTransferBusy(false)
            }
        },
        [clientShareRecord?.deviceId, upsertDeviceTransferSession],
    )

    const handleConfirmDeviceTransferSas = useCallback(async () => {
        if (!activeDeviceTransferSession || !activeDeviceTransferRole) {
            return
        }

        const deviceId =
            activeDeviceTransferRole === 'requester'
                ? localDeviceId
                : clientShareRecord?.deviceId || ''

        if (!deviceId || !activeDeviceTransferSasNumbers || !activeDeviceTransferConfirmKey) {
            return
        }

        try {
            setDeviceTransferBusy(true)
            setDeviceTransferError('')
            const sasProof = await computeDeviceTransferSasProof(
                activeDeviceTransferConfirmKey,
                activeDeviceTransferRole,
                activeDeviceTransferSasNumbers,
                transcriptForSession(activeDeviceTransferSession),
            )
            const confirmResult = await api.confirmBsvDeviceTransferSessionSas(
                activeDeviceTransferSession.id,
                deviceId,
                sasProof,
            )

            if (confirmResult.session) {
                upsertDeviceTransferSession(confirmResult.session)
            }

            if (confirmResult.error || !confirmResult.session) {
                throw new Error(confirmResult.error || 'Failed to confirm matching SAS numbers')
            }

            setLocallyConfirmedSessionId(confirmationScope(activeDeviceTransferSession))

            setDeviceTransferSuccess(
                'This device confirmed the numbers. Waiting for the other device to confirm the same set.',
            )
        } catch (error) {
            setDeviceTransferError(
                error instanceof Error ? error.message : 'Failed to confirm matching SAS numbers',
            )
        } finally {
            setDeviceTransferBusy(false)
        }
    }, [
        activeDeviceTransferConfirmKey,
        activeDeviceTransferRole,
        activeDeviceTransferSasNumbers,
        activeDeviceTransferSession,
        clientShareRecord?.deviceId,
        localDeviceId,
        upsertDeviceTransferSession,
    ])

    const handleCancelActiveDeviceTransfer = useCallback(async () => {
        if (!activeDeviceTransferSession || !activeDeviceTransferRole) {
            return
        }

        const deviceId =
            activeDeviceTransferRole === 'requester'
                ? localDeviceId
                : clientShareRecord?.deviceId || ''

        if (!deviceId) {
            return
        }

        try {
            setDeviceTransferBusy(true)
            setDeviceTransferError('')
            const cancelResult =
                activeDeviceTransferRole === 'approver'
                    ? await api.releaseBsvDeviceTransferSession(
                          activeDeviceTransferSession.id,
                          deviceId,
                      )
                    : await api.cancelBsvDeviceTransferSession(
                          activeDeviceTransferSession.id,
                          deviceId,
                      )

            if (cancelResult.session) {
                upsertDeviceTransferSession(cancelResult.session)
            }

            if (cancelResult.error) {
                throw new Error(cancelResult.error)
            }

            if (activeDeviceTransferRole === 'approver') {
                setDeviceTransferSuccess(
                    'This browser released the request. The new device is now waiting for another trusted device.',
                )
            } else {
                setDeviceTransferSuccess('This transfer request was cancelled.')
            }
            clearActiveDeviceTransfer()
        } catch (error) {
            setDeviceTransferError(
                error instanceof Error ? error.message : 'Failed to cancel device transfer',
            )
        } finally {
            setDeviceTransferBusy(false)
        }
    }, [
        activeDeviceTransferRole,
        activeDeviceTransferSession,
        clearActiveDeviceTransfer,
        clientShareRecord?.deviceId,
        localDeviceId,
        upsertDeviceTransferSession,
    ])

    const deviceTransferStatusMessage = useMemo(() => {
        if (!activeDeviceTransferSession || !activeDeviceTransferRole) {
            return ''
        }

        if (
            activeDeviceTransferRole === 'requester' &&
            activeDeviceTransferSession.status === 'pending'
        ) {
            return 'Waiting for another trusted device to accept this request.'
        }

        if (!localDeviceTransferConfirmed && activeDeviceTransferSasNumbers) {
            return 'Check that both devices show the same three numbers, then confirm on both sides.'
        }

        if (
            localDeviceTransferConfirmed &&
            !(
                activeDeviceTransferSession.requester_sas_confirmed &&
                activeDeviceTransferSession.approver_sas_confirmed
            )
        ) {
            return 'Waiting for the other device to confirm the same three numbers.'
        }

        if (activeDeviceTransferRole === 'approver' && !activeDeviceTransferSession.payload_ready) {
            return 'Both sides confirmed. This device is encrypting and uploading the wallet now.'
        }

        if (activeDeviceTransferRole === 'requester' && activeDeviceTransferSession.payload_ready) {
            return 'The encrypted wallet is ready. This browser is restoring its own local share pair now.'
        }

        if (activeDeviceTransferRole === 'requester') {
            return 'Waiting for the approved device to send the encrypted wallet payload.'
        }

        return 'Waiting for the new device to finish restoring and activating its own local share pair.'
    }, [
        activeDeviceTransferRole,
        activeDeviceTransferSasNumbers,
        activeDeviceTransferSession,
        localDeviceTransferConfirmed,
    ])

    const buyDepositAddress =
        currentUserBsvWalletType === BsvWalletType.PANDA
            ? pandaDisplayAddress
            : currentUserBsvAddress
    const hasActiveBuyTransactions = useMemo(
        () =>
            buyHistory.some(tx => tx?.external_id && !GUARDARIAN_TERMINAL_STATUSES.has(tx.status)),
        [buyHistory],
    )
    const visibleStatusTransactions = useMemo(
        () =>
            buyHistory.filter(tx => {
                if (!tx?.external_id) return false
                if (GUARDARIAN_IN_PROGRESS_STATUSES.has(tx.status)) return true
                if (tx.status !== GUARDARIAN_FAILED_STATUS) return false

                const failedAtMs = Date.parse(tx.failed_at || tx.updated_at || tx.created_at || '')
                if (Number.isNaN(failedAtMs)) return false
                return statusNowMs - failedAtMs <= GUARDARIAN_FAILED_VISIBLE_MS
            }),
        [buyHistory, statusNowMs],
    )

    useEffect(() => {
        if (!hasBuyBsvFeature) return
        const interval = setInterval(() => setStatusNowMs(Date.now()), 30_000)
        return () => clearInterval(interval)
    }, [hasBuyBsvFeature])

    const buyButtonDisabled =
        buyPreparing || !buyDepositAddress || (!buyCheckoutUrl && hasActiveBuyTransactions)

    async function handleBuyButtonClick() {
        if (!hasBuyBsvFeature || !buyDepositAddress || buyPreparing || buyCreateInFlightRef.current)
            return

        if (buyCheckoutUrl) {
            handleCopySync(buyDepositAddress, 'bsv')
            setBuyTrayOpen(true)
            return
        }

        if (hasActiveBuyTransactions) return

        buyCreateInFlightRef.current = true
        setBuyPreparing(true)
        setBuyError('')

        const makeReturnUrl = (noticeType: 'success' | 'error') => {
            const url = new URL(window.location.href)
            url.searchParams.set('open_wallet', '1')
            url.searchParams.set('wallet_notice_type', noticeType)
            url.searchParams.set(
                'wallet_notice',
                noticeType === 'success'
                    ? 'BSV purchase completed successfully.'
                    : 'BSV purchase was cancelled or failed.',
            )
            return url.toString()
        }

        try {
            const res = await api.guardarianCreateTransaction({
                fromCurrency: 'USD',
                toCurrency: 'BSV',
                amount: buyAmount,
                address: buyDepositAddress,
                redirectUrl: window.location.href,
                successUrl: makeReturnUrl('success'),
                failureUrl: makeReturnUrl('error'),
            })

            if (!walletModalMountedRef.current) return

            const checkoutUrl = res?.checkout_url || null
            const externalId = res?.external_id || ''
            if (res?.error || !checkoutUrl) {
                setBuyError(res?.error || 'Failed to prepare checkout.')
                return
            }

            setBuyCheckoutUrl(checkoutUrl)
            setBuyTxId(externalId)

            if (externalId) {
                setBuyHistory(prev => [res, ...prev])
                pendingTxIdsRef.current.add(externalId)
                setHistoryPolling(true)
            }

            handleCopySync(buyDepositAddress, 'bsv')
            setBuyTrayOpen(true)
        } catch (e: any) {
            if (walletModalMountedRef.current) {
                setBuyError(e?.message || 'Failed to prepare checkout.')
            }
        } finally {
            buyCreateInFlightRef.current = false
            if (walletModalMountedRef.current) {
                setBuyPreparing(false)
            }
        }
    }

    // When opened directly into the buy flow (e.g. from the "Buy $20 of
    // Bitcoin SV" prompt), jump straight to the payment tray once the checkout
    // URL is ready instead of leaving the user on the wallet screen.
    const autoBuyTrayRef = useRef(false)
    useEffect(() => {
        if (!modalParams?.openBuyTray || autoBuyTrayRef.current) return
        if (!hasBuyBsvFeature) return
        if (!buyCheckoutUrl) {
            if (
                !buyDepositAddress ||
                buyPreparing ||
                buyCreateInFlightRef.current ||
                hasActiveBuyTransactions
            )
                return

            autoBuyTrayRef.current = true
            void handleBuyButtonClick()
            return
        }

        autoBuyTrayRef.current = true
        setBuyTrayOpen(true)
    }, [
        modalParams?.openBuyTray,
        hasBuyBsvFeature,
        buyCheckoutUrl,
        buyDepositAddress,
        buyPreparing,
        hasActiveBuyTransactions,
    ])

    function handleCheckoutNavigate() {
        setBuyTrayOpen(false)
        try {
            window.open(buyCheckoutUrl, '_blank', 'noopener,noreferrer')
        } catch (_) {}
    }

    function handleOpenCheckoutForTx(checkoutUrl: string) {
        handleCopySync(buyDepositAddress, 'bsv')
        try {
            window.open(checkoutUrl, '_blank', 'noopener,noreferrer')
        } catch (_) {}
    }

    async function toggleBuyHistory() {
        const opening = !buyHistoryOpen
        setBuyHistoryOpen(opening)
        if (opening && buyHistory.length === 0) {
            setBuyHistoryLoading(true)
            try {
                const txs = await api.guardarianListTransactions(5)
                if (Array.isArray(txs)) setBuyHistory(txs)
            } catch (_) {}
            setBuyHistoryLoading(false)
        }
    }

    // Conservative buffer (sats) reserved for the mining fee so the confirmed-balance gate
    // doesn't approve a send that then fails funding server-side.
    const SEND_FEE_BUFFER_SATS = 500

    // Reuse the same payment_id while (recipient, amount) is unchanged; mint a fresh one when
    // the form changes. Makes a re-click after a swallowed error an idempotent retry.
    function paymentIdForCurrentSend(destinationAddress: string, amount: number): string {
        const key = `${destinationAddress}:${amount}`
        if (!paymentIntentRef.current || paymentIntentRef.current.key !== key) {
            paymentIntentRef.current = { id: crypto.randomUUID(), key }
        }
        return paymentIntentRef.current.id
    }

    function trackPaymentToTerminal(paymentId: string) {
        // Tear down any prior tracker first.
        if (paymentTrackingRef.current) {
            paymentTrackingRef.current.unsubscribe()
            window.clearTimeout(paymentTrackingRef.current.timeout)
            paymentTrackingRef.current = null
        }

        const finish = () => {
            if (!paymentTrackingRef.current) return
            paymentTrackingRef.current.unsubscribe()
            window.clearTimeout(paymentTrackingRef.current.timeout)
            paymentTrackingRef.current = null
            setSending(false)
        }

        const kind = `payment-${paymentId}`
        // In-flight status toast (spinner, no auto-dismiss) that we resolve in place below.
        toast.pending('Sending payment…', { kind, testId: 'toast-wallet-send' })

        const unsubscribe = subscribePaymentUpdate(paymentId, (update: PaymentUpdate) => {
            if (!isTerminalPaymentStatus(update.status)) return
            // Any terminal outcome frees the intent id so the next send is a fresh attempt.
            paymentIntentRef.current = null
            if (isSuccessfulPaymentStatus(update.status)) {
                setAmountInput('')
                toast.update(
                    kind,
                    update.txId ? `Payment sent · ${shortTxid(update.txId)}` : 'Payment sent',
                    { variant: 'success' },
                )
            } else if (update.status === 'failed') {
                toast.update(kind, `Payment failed: ${update.lastError || 'unknown error'}`, {
                    variant: 'error',
                })
            } else if (update.status === 'cancelled') {
                toast.update(kind, 'Payment cancelled', { variant: 'info' })
            }
            finish()
        })

        // Safety net: if no terminal update arrives, release the button and point the user
        // at their history rather than leaving a stuck spinner. Self-custody (client-signing)
        // sends wait for the payer's device to sign (up to ~10 min server-side) before
        // broadcasting, so give them a wider window than the server-signing/legacy path.
        const features = (gon.currentUser?.features as unknown[]) || []
        const hasClientSigning = features.some(
            f =>
                (typeof f === 'string' ? f : (f as { name?: string })?.name) ===
                'use_client_signing',
        )
        const trackTimeoutMs = (hasClientSigning ? 15 : 3) * 60 * 1000
        const timeout = window.setTimeout(() => {
            if (!paymentTrackingRef.current) return
            paymentTrackingRef.current.unsubscribe()
            paymentTrackingRef.current = null
            setSending(false)
            toast.update(kind, 'Still processing — check Payments history for status', {
                variant: 'info',
            })
        }, trackTimeoutMs)

        paymentTrackingRef.current = { unsubscribe, timeout }
    }

    async function handleSend() {
        if (sending) return

        // Keep the send boundary defensive even though both amount input modes normalize to sats.
        const amount = Number(amountSats)
        if (!Number.isSafeInteger(amount) || amount <= 0) {
            toast.error('Enter a valid amount greater than zero')
            return
        }

        // Fund from confirmed balance only (server funds confirmed UTXOs), leaving a fee buffer.
        const confirmedBalance =
            walletDetailsData?.confirmedBalance != null
                ? Number(walletDetailsData.confirmedBalance)
                : currentUserBsvBalance
        if (amount + SEND_FEE_BUFFER_SATS > confirmedBalance) {
            toast.error('Insufficient confirmed funds (amount plus network fee)')
            return
        }

        const isValidBsvAddrRegEx = /^(1|3)[A-HJ-NP-Za-km-z1-9]{25,34}$/
        const isUsername = /^@/.test(address) && usernameRegex.test(address.slice(1))

        if (!isValidBsvAddrRegEx.test(address) && !isUsername) {
            toast.error('Enter a valid BSV address or @username')
            return
        }

        let destinationAddress = address
        if (isUsername) {
            const userAddress = await api.getBsvAddressForUser(address.slice(1))
            if (!userAddress?.address) {
                toast.error(userAddress?.error || `Could not resolve ${address}`)
                return
            }
            destinationAddress = userAddress.address
        }

        if (!isValidBsvAddrRegEx.test(destinationAddress)) {
            toast.error('Could not resolve a valid destination address')
            return
        }

        const okToSend = await confirmBsvPaymentTray([
            {
                recipient: isUsername ? address : null,
                address: destinationAddress,
                amountSats: amount,
            },
        ])
        if (!okToSend) return

        setSending(true)
        const paymentId = paymentIdForCurrentSend(destinationAddress, amount)
        const kind = `payment-${paymentId}`
        const toTreechatUsers = isUsername ? [address.slice(1)] : undefined

        // Begin tracking BEFORE sending so we never miss a fast terminal update. This also
        // raises the pending "Sending payment…" toast, which the branches below resolve.
        trackPaymentToTerminal(paymentId)

        let sendResult
        try {
            sendResult = await wallet.sendBsv(
                destinationAddress,
                amount,
                undefined,
                toTreechatUsers,
                paymentId,
            )
        } catch (error) {
            // e.g. missing self-custody key share threw out of the wallet.
            if (paymentTrackingRef.current) {
                paymentTrackingRef.current.unsubscribe()
                window.clearTimeout(paymentTrackingRef.current.timeout)
                paymentTrackingRef.current = null
            }
            setSending(false)
            toast.update(
                kind,
                `Could not send: ${error instanceof Error ? error.message : 'unknown error'}`,
                {
                    variant: 'error',
                },
            )
            return
        }

        const stopTracking = () => {
            if (paymentTrackingRef.current) {
                paymentTrackingRef.current.unsubscribe()
                window.clearTimeout(paymentTrackingRef.current.timeout)
                paymentTrackingRef.current = null
            }
            setSending(false)
        }

        const status = sendResult?.status
        // Accepted for processing: keep `sending` true and the pending toast up, and wait for
        // the terminal bsv_payment_update the tracker is listening for.
        if (['temporal_pending', 'in progress', 'ok', 'pending'].includes(status || '')) {
            return
        }

        // Synchronous terminal outcomes (idempotent re-send of an already-resolved payment_id).
        if (status === 'completed') {
            paymentIntentRef.current = null
            setAmountInput('')
            stopTracking()
            toast.update(
                kind,
                sendResult?.tx_id
                    ? `Payment sent · ${shortTxid(sendResult.tx_id)}`
                    : 'Payment sent',
                { variant: 'success' },
            )
            return
        }
        if (status === 'failed') {
            // Free the id so the user can start a fresh attempt.
            paymentIntentRef.current = null
            stopTracking()
            toast.update(kind, 'That payment previously failed — please try again', {
                variant: 'error',
            })
            return
        }
        if (status === 'conflict') {
            stopTracking()
            toast.update(kind, 'This payment is already being processed', { variant: 'info' })
            return
        }

        // Network error / 4xx / 5xx: keep the id so a re-click is an idempotent retry.
        stopTracking()
        toast.update(kind, `Could not send: ${sendResult?.error || 'unknown error'}`, {
            variant: 'error',
        })
    }

    // `convert` off re-denominates the digits already typed rather than preserving their value:
    // someone who typed 0.001 into a sats field meant 0.001 BSV, not 0.001 sats' worth of BSV.
    function selectAmountUnit(nextUnit: BsvAmountUnit, { convert = true } = {}) {
        if (nextUnit === amountUnit) return

        if (convert) {
            setAmountInput(currentValue =>
                convertBsvAmountInputUnit(currentValue, amountUnit, nextUnit),
            )
        }
        setAmountUnit(nextUnit)
    }

    // The line under the amount field. It always spells out the selected unit — as a live
    // both-units echo once the amount parses, and as an explanation when it doesn't — so the
    // unit is never something you have to infer from which chip looks lit.
    function amountHint(): { warning: boolean; content: React.ReactNode } {
        if (amountState.status === 'empty') {
            return {
                warning: false,
                content:
                    amountUnit === 'bsv'
                        ? 'Entering BSV — 1 BSV = 100,000,000 sats'
                        : 'Entering sats — 100,000,000 sats = 1 BSV',
            }
        }

        if (amountState.status === 'fractional-sats') {
            return {
                warning: true,
                content: (
                    <>
                        sats are whole numbers —{' '}
                        <button
                            type="button"
                            className={styles.amountHintAction}
                            onClick={() => selectAmountUnit('bsv', { convert: false })}
                        >
                            enter this in BSV
                        </button>{' '}
                        instead.
                    </>
                ),
            }
        }

        if (amountState.status === 'sub-satoshi-bsv') {
            return {
                warning: true,
                content: 'BSV goes to 8 decimals — 1 satoshi is the smallest amount.',
            }
        }

        if (amountState.status === 'invalid') {
            return { warning: true, content: `Enter a valid amount in ${unitLabel(amountUnit)}.` }
        }

        if (amountState.status === 'zero') {
            return { warning: true, content: 'Enter an amount greater than zero.' }
        }

        const amountSats = amountState.amountSats
        const inBsv = `${toBitcoin(amountSats)} BSV`
        const inSats = `${amountSats.toLocaleString()} sats`
        return {
            warning: false,
            content: (
                <>
                    Sending{' '}
                    <strong className={styles.amountHintPrimary}>
                        {amountUnit === 'bsv' ? inBsv : inSats}
                    </strong>{' '}
                    = {amountUnit === 'bsv' ? inSats : inBsv}
                    {amountUsd != null && ` · ≈ ${formatUsdValue(amountUsd)}`}
                </>
            ),
        }
    }

    const sendAmountHint = amountHint()

    return (
        <div className={styles.walletComp}>
            <div className={styles.tabsBar}>
                <button
                    className={cn(styles.tabButton, activeTab === 'bsv' && styles.tabActive)}
                    onClick={() => setActiveTab('bsv')}
                >
                    <WalletIcon size={16} style={{ marginRight: 8 }} />
                    BSV
                </button>
                <button
                    className={cn(styles.tabButton, activeTab === 'stripe' && styles.tabActive)}
                    onClick={() => setActiveTab('stripe')}
                >
                    <CreditCard size={16} style={{ marginRight: 8 }} />
                    USD
                </button>
            </div>

            <div className={styles.tabContent}>
                {activeTab === 'bsv' && (
                    <>
                        <div className={styles.balanceContainer}>
                            <div className={cn(styles.bsvIcon, useUsd && styles.usdIcon)}>
                                {useUsd ? 'USD' : <i className="fa fa-btc" />}
                            </div>
                            <div className={styles.amount}>
                                {hasBalanceLoaded
                                    ? useUsd
                                        ? formatSatsAmount(currentUserBsvBalance, {
                                              includeUnit: false,
                                              compactSats: false,
                                          })
                                        : toBitcoin(currentUserBsvBalance)
                                    : '...'}
                            </div>
                            <div className={styles.usdAmount}>
                                {hasBalanceLoaded
                                    ? useUsd
                                        ? `${toBitcoin(currentUserBsvBalance)} BSV · ${Math.round(
                                              currentUserBsvBalance,
                                          ).toLocaleString()} sats`
                                        : `${formatUsdValue(bsvBalanceUsd)} USD`
                                    : '...'}
                            </div>

                            <div className={styles.confirmedUnconfirmedContainer}>
                                <div>
                                    Confirmed:{' '}
                                    <strong>
                                        {walletDetailsData?.confirmedBalance != null
                                            ? useUsd
                                                ? `${formatSatsAmount(
                                                      walletDetailsData.confirmedBalance,
                                                      {
                                                          includeUnit: false,
                                                          compactSats: false,
                                                      },
                                                  )} (${toBitcoin(
                                                      walletDetailsData.confirmedBalance,
                                                  )} BSV)`
                                                : toBitcoin(walletDetailsData.confirmedBalance)
                                            : '...'}
                                    </strong>
                                </div>
                                <div>
                                    Unconfirmed:{' '}
                                    <strong>
                                        {walletDetailsData?.unconfirmedBalance != null
                                            ? useUsd
                                                ? `${formatSatsAmount(
                                                      walletDetailsData.unconfirmedBalance,
                                                      {
                                                          includeUnit: false,
                                                          compactSats: false,
                                                      },
                                                  )} (${toBitcoin(
                                                      walletDetailsData.unconfirmedBalance,
                                                  )} BSV)`
                                                : toBitcoin(walletDetailsData.unconfirmedBalance)
                                            : '...'}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        {showDevClientSetup && (
                            <WalletFileCheckPill status={localWalletStatus} onOpen={closeModal} />
                        )}

                        <WalletSwap
                            address={buyDepositAddress}
                            usdPerBsv={bsvUsdRate}
                            openSwapId={modalParams?.openSwapId}
                        />

                        <WalletDailyEarnings closeModal={closeModal} />

                        <WalletSeeds />

                        {hasBuyBsvFeature && (
                            <>
                                <div className={styles.buyBsvRow}>
                                    <button
                                        className={styles.buyBsvButton}
                                        disabled={buyButtonDisabled}
                                        onClick={handleBuyButtonClick}
                                    >
                                        {buyPreparing && (
                                            <Loader2 size={18} className={styles.buySpinner} />
                                        )}
                                        <span>
                                            {buyPreparing
                                                ? 'Preparing checkout...'
                                                : `Buy $${buyAmount} of BSV`}
                                        </span>
                                    </button>
                                    {SHOW_BUY_HISTORY_UI && (
                                        <button
                                            className={cn(
                                                styles.buyHistoryToggle,
                                                buyHistoryOpen && styles.buyHistoryToggleOpen,
                                            )}
                                            onClick={toggleBuyHistory}
                                            title="Recent purchases"
                                        >
                                            <ChevronDown size={20} />
                                        </button>
                                    )}
                                </div>
                                <div className={styles.buyKycNote}>Requires KYC</div>
                            </>
                        )}

                        {SHOW_BUY_HISTORY_UI && hasBuyBsvFeature && buyHistoryOpen && (
                            <div className={styles.buyHistoryList}>
                                {buyHistoryLoading ? (
                                    <div className={styles.buyHistoryLoading}>
                                        <Loader2 size={16} className={styles.buySpinner} />
                                        <span>Loading...</span>
                                    </div>
                                ) : buyHistory.filter(tx =>
                                      GUARDARIAN_TERMINAL_STATUSES.has(tx.status),
                                  ).length === 0 ? (
                                    <div className={styles.buyHistoryEmpty}>
                                        No recent purchases
                                    </div>
                                ) : (
                                    buyHistory
                                        .filter(tx => GUARDARIAN_TERMINAL_STATUSES.has(tx.status))
                                        .map(tx => (
                                            <div key={tx.id} className={styles.buyHistoryItem}>
                                                <div className={styles.buyHistoryDate}>
                                                    {tx.created_at
                                                        ? new Date(
                                                              tx.created_at,
                                                          ).toLocaleDateString()
                                                        : '—'}
                                                </div>
                                                <div className={styles.buyHistoryAmount}>
                                                    $
                                                    {tx.from_amount != null
                                                        ? Number(tx.from_amount).toFixed(2)
                                                        : '—'}
                                                </div>
                                                <div
                                                    className={cn(
                                                        styles.buyHistoryStatus,
                                                        tx.status === 'finished' &&
                                                            styles.buyHistoryStatusSuccess,
                                                        GUARDARIAN_TERMINAL_STATUSES.has(
                                                            tx.status,
                                                        ) &&
                                                            tx.status !== 'finished' &&
                                                            tx.status !== 'created' &&
                                                            styles.buyHistoryStatusFailed,
                                                    )}
                                                >
                                                    {GUARDARIAN_STATUS_LABELS[tx.status] ||
                                                        tx.status ||
                                                        '—'}
                                                </div>
                                                {tx.checkout_url && (
                                                    <a
                                                        className={styles.buyHistoryLink}
                                                        href={tx.checkout_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open transaction"
                                                    >
                                                        <ExternalLink size={14} />
                                                    </a>
                                                )}
                                            </div>
                                        ))
                                )}
                            </div>
                        )}

                        {hasBuyBsvFeature && !!buyError && (
                            <div className={styles.buyError}>{buyError}</div>
                        )}

                        {hasBuyBsvFeature && visibleStatusTransactions.length > 0 && (
                            <div className={styles.buyStatusBox}>
                                {visibleStatusTransactions.map((tx, index) => (
                                    <div
                                        key={tx.id || tx.external_id}
                                        className={styles.buyStatusRow}
                                        style={
                                            index < visibleStatusTransactions.length - 1
                                                ? { marginBottom: 8 }
                                                : undefined
                                        }
                                    >
                                        {GUARDARIAN_IN_PROGRESS_STATUSES.has(tx.status) ? (
                                            <Loader2 size={18} className={styles.buySpinner} />
                                        ) : null}
                                        <div className={styles.buyStatusText}>
                                            {GUARDARIAN_STATUS_LABELS[tx.status] ||
                                                tx.status ||
                                                'Checking...'}
                                        </div>
                                        {tx.checkout_url &&
                                            GUARDARIAN_IN_PROGRESS_STATUSES.has(tx.status) && (
                                                <button
                                                    className={styles.secondaryBtn}
                                                    style={{ marginLeft: 'auto' }}
                                                    onClick={() =>
                                                        handleOpenCheckoutForTx(tx.checkout_url)
                                                    }
                                                >
                                                    Open checkout
                                                </button>
                                            )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {hasBuyBsvFeature && buyTrayOpen && (
                            <Tray onDismiss={() => setBuyTrayOpen(false)} title="Buy BSV">
                                <div className={styles.buyTrayBody}>
                                    <div className={styles.buyTrayStep}>
                                        <div className={styles.buyTrayStepNumber}>1</div>
                                        <div className={styles.buyTrayStepContent}>
                                            <div className={styles.buyTrayLabel}>
                                                <strong>Receive</strong> funds at your address
                                            </div>
                                            <div className={styles.buyAddress}>
                                                <span>{buyDepositAddress}</span>
                                                <button
                                                    className={styles.buyCopiedBadge}
                                                    onClick={() =>
                                                        handleCopy(buyDepositAddress, 'bsv')
                                                    }
                                                >
                                                    <Check size={14} />
                                                    <span>copied</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.buyTrayStep}>
                                        <div className={styles.buyTrayStepNumber}>2</div>
                                        <div className={styles.buyTrayStepContent}>
                                            <div className={styles.buyTrayLabel}>
                                                <strong>Checkout</strong> — paste your address when
                                                asked
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.buyTrayStep}>
                                        <div className={styles.buyTrayStepNumber}>3</div>
                                        <div className={styles.buyTrayStepContent}>
                                            <div className={styles.buyTrayLabel}>
                                                <strong>Return</strong> when finished paying — no
                                                need to wait until send confirms.
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        className={styles.sendBtn}
                                        style={{
                                            width: '100%',
                                            marginTop: 8,
                                            background: 'var(--color-brand-bitcoin)',
                                        }}
                                        onClick={handleCheckoutNavigate}
                                    >
                                        Go to Checkout
                                    </button>
                                </div>
                            </Tray>
                        )}

                        {DEBUG && (
                            <div
                                className={styles.refragmentButton}
                                onClick={async e => {
                                    e.preventDefault()
                                    if (isFragmenting) return

                                    try {
                                        setIsFragmenting(true)
                                        const response = await api.createFragmentWalletTx(
                                            10,
                                            0.5,
                                            true,
                                        )

                                        if (response.error) {
                                            alert(`Error fragmenting wallet: ${response.error}`)
                                        } else if (response.broadcast_result?.error) {
                                            alert(
                                                `Error broadcasting transaction: ${response.broadcast_result.error}`,
                                            )
                                        } else {
                                            alert('Wallet successfully refragmented')
                                            // refresh balance after fragmenting
                                            wallet?.getBalance()
                                        }
                                    } catch (err) {
                                        console.error('Error fragmenting wallet:', err)
                                        alert('Failed to fragment wallet. Please try again.')
                                    } finally {
                                        setIsFragmenting(false)
                                    }
                                }}
                            >
                                <Activity size={16} className={styles.iconStyle} />
                                <span className={styles.refragmentText}>
                                    {isFragmenting ? 'Fragmenting...' : 'Refragment Wallet'}
                                </span>
                            </div>
                        )}

                        <div className={styles.depositContainer}>
                            <div className={styles.depositHeader}>
                                <ArrowDownLeft size={20} /> Receive
                            </div>

                            <div className={styles.addressRow}>
                                <div className={styles.addressLabel}>BSV Address</div>
                                {(() => {
                                    const addr =
                                        currentUserBsvWalletType === BsvWalletType.PANDA
                                            ? pandaDisplayAddress
                                            : currentUserBsvAddress
                                    return (
                                        <div className={styles.addressDisplay}>
                                            <a
                                                href={`https://whatsonchain.com/address/${addr}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {addr || 'Loading...'}
                                            </a>
                                            <button
                                                className={cn(
                                                    styles.clipboardButton,
                                                    copiedAddress === 'bsv' &&
                                                        styles.clipboardClick,
                                                )}
                                                onClick={() => handleCopy(addr, 'bsv')}
                                            >
                                                {copiedAddress === 'bsv' ? (
                                                    <Check size={16} />
                                                ) : (
                                                    <Copy size={16} />
                                                )}
                                            </button>
                                        </div>
                                    )
                                })()}
                            </div>

                            {currentUserBsvWalletType === BsvWalletType.SHUALLET && (
                                <div className={styles.addressRow}>
                                    <div className={styles.addressLabel}>Ordinals Address</div>
                                    <div className={styles.addressDisplay}>
                                        <a
                                            href={`https://whatsonchain.com/address/${currentUserBsvOrdAddress}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {currentUserBsvOrdAddress || 'Loading...'}
                                        </a>
                                        <button
                                            className={cn(
                                                styles.clipboardButton,
                                                copiedAddress === 'ord' && styles.clipboardClick,
                                            )}
                                            onClick={() =>
                                                handleCopy(currentUserBsvOrdAddress, 'ord')
                                            }
                                        >
                                            {copiedAddress === 'ord' ? (
                                                <Check size={16} />
                                            ) : (
                                                <Copy size={16} />
                                            )}
                                        </button>
                                    </div>
                                    <OrdinalsStrayFunds className={styles.ordinalsStrayFunds} />
                                </div>
                            )}
                        </div>

                        {/* Browser-wallet accounts: one card for the file, passkey and device. */}
                        {showDevClientSetup && isLocalWalletAccount === true && (
                            <WalletSafetyCard
                                status={localWalletStatus}
                                userId={currentUserId}
                                deviceHasKeys={
                                    clientShareChecked
                                        ? Boolean(clientShareRecord?.clientShare)
                                        : undefined
                                }
                                onOpen={closeModal}
                                onSaved={recordLocalWalletFileSaved}
                            />
                        )}

                        {showDevClientSetup && isLocalWalletAccount === false && (
                            <WalletFileBackup
                                key={`file-${currentUserId}`}
                                userId={currentUserId}
                                canExport={
                                    clientShareChecked
                                        ? Boolean(clientShareRecord?.clientShare)
                                        : undefined
                                }
                            />
                        )}

                        {showDevClientSetup && isLocalWalletAccount === false && (
                            <WalletPasskeyRecovery
                                key={currentUserId}
                                userId={currentUserId}
                                serverBackup
                            />
                        )}

                        {/* Only once the account is known not to be a browser wallet: never
                            while that check is loading or failed. */}
                        {showDevClientSetup && isLocalWalletAccount === false && (
                            <div className={styles.selfCustodySetupCard}>
                                <div className={styles.selfCustodySetupHeader}>
                                    <Lock size={20} />
                                    <div>
                                        <div className={styles.selfCustodySetupTitle}>
                                            Dev Self-Custody Device Setup
                                        </div>
                                        <div className={styles.selfCustodySetupSubtitle}>
                                            Bootstrap this browser directly, or hand it an encrypted
                                            wallet transfer from another trusted device.
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.selfCustodySetupActions}>
                                    {!clientShareRecord && canRequestTransferFromAnotherDevice && (
                                        <button
                                            className={styles.secondaryBtn}
                                            disabled={setupBusy || !localDeviceId}
                                            onClick={() =>
                                                openDeviceTransferModal({
                                                    role: 'requester',
                                                    autoStart: true,
                                                    launchSource: 'wallet',
                                                    requestingDeviceId: localDeviceId,
                                                })
                                            }
                                        >
                                            Request From Another Device
                                        </button>
                                    )}

                                    {!clientShareRecord && (
                                        <button
                                            className={styles.secondaryBtn}
                                            disabled={
                                                setupBusy ||
                                                clientShareStatusLoading ||
                                                deviceTransferBusy ||
                                                Boolean(activeDeviceTransferSession)
                                            }
                                            onClick={() => handleGenerateClientSetup('generate')}
                                        >
                                            {setupBusy && setupAction === 'generate'
                                                ? 'Generating...'
                                                : 'Generate Device Setup'}
                                        </button>
                                    )}

                                    {canRegisterExistingDevice && (
                                        <button
                                            className={styles.secondaryBtn}
                                            disabled={
                                                setupBusy ||
                                                clientShareStatusLoading ||
                                                deviceTransferBusy ||
                                                Boolean(activeDeviceTransferSession)
                                            }
                                            onClick={() => handleGenerateClientSetup('register')}
                                        >
                                            {setupBusy && setupAction === 'register'
                                                ? 'Registering...'
                                                : 'Register This Device'}
                                        </button>
                                    )}

                                    {showReplaceSetupAction && (
                                        <button
                                            className={styles.secondaryBtn}
                                            disabled={
                                                setupBusy ||
                                                clientShareStatusLoading ||
                                                deviceTransferBusy ||
                                                Boolean(activeDeviceTransferSession)
                                            }
                                            onClick={() => handleGenerateClientSetup('replace')}
                                        >
                                            {setupBusy && setupAction === 'replace'
                                                ? 'Replacing...'
                                                : 'Replace This Browser Setup'}
                                        </button>
                                    )}

                                    <button
                                        className={styles.secondaryBtn}
                                        disabled={
                                            clientShareStatusLoading ||
                                            setupBusy ||
                                            deviceTransferBusy
                                        }
                                        onClick={() => refreshClientShareStatus()}
                                    >
                                        {clientShareStatusLoading
                                            ? 'Refreshing...'
                                            : 'Refresh Status'}
                                    </button>
                                </div>

                                <div className={styles.selfCustodySetupHint}>
                                    {selfCustodySetupHint}
                                </div>

                                {clientShareStatusError && (
                                    <div className={styles.selfCustodySetupError}>
                                        {clientShareStatusError}
                                    </div>
                                )}

                                {setupError && (
                                    <div className={styles.selfCustodySetupError}>{setupError}</div>
                                )}

                                {setupSuccess && (
                                    <div className={styles.selfCustodySetupGenerated}>
                                        <div className={styles.selfCustodySetupGeneratedTitle}>
                                            Setup Complete
                                        </div>
                                        <div className={styles.selfCustodySetupHint}>
                                            {setupSuccess}
                                        </div>
                                    </div>
                                )}

                                <div className={styles.selfCustodySetupHint}>
                                    If this wallet already works on another device, bring it over
                                    from there. Treechat will open a separate secure setup window
                                    when it is time to approve the transfer and compare the numbers.
                                </div>

                                <div className={styles.selfCustodySetupStatusGrid}>
                                    <div className={styles.selfCustodySetupStatusCard}>
                                        <div className={styles.selfCustodySetupStatusLabel}>
                                            Local Client Share
                                        </div>
                                        <div className={styles.selfCustodySetupStatusValue}>
                                            {clientShareRecord
                                                ? 'Stored on this browser'
                                                : 'Not set up'}
                                        </div>
                                        {localDeviceId && !clientShareRecord && (
                                            <div className={styles.selfCustodySetupField}>
                                                <div className={styles.selfCustodySetupFieldLabel}>
                                                    Reserved Device ID
                                                </div>
                                                <div className={styles.selfCustodySetupFieldRow}>
                                                    <code>{localDeviceId}</code>
                                                    <button
                                                        className={styles.clipboardButton}
                                                        onClick={() =>
                                                            handleCopySetupField(
                                                                localDeviceId,
                                                                'reserved-device-id',
                                                            )
                                                        }
                                                    >
                                                        {copiedSetupField ===
                                                        'reserved-device-id' ? (
                                                            <Check size={16} />
                                                        ) : (
                                                            <Copy size={16} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {clientShareRecord && (
                                            <>
                                                <div className={styles.selfCustodySetupField}>
                                                    <div
                                                        className={
                                                            styles.selfCustodySetupFieldLabel
                                                        }
                                                    >
                                                        Local Wallet Package
                                                    </div>
                                                    <div className={styles.selfCustodySetupHint}>
                                                        {localWalletPayloadStatus === 'compatible'
                                                            ? 'Stored in the new full-wallet self-custody format.'
                                                            : localWalletPayloadStatus === 'legacy'
                                                            ? 'This browser needs re-setup before it can recover the wallet locally.'
                                                            : localWalletPayloadStatus === 'missing'
                                                            ? 'This browser is missing its local full-wallet package and needs re-setup.'
                                                            : 'Refresh status to verify the local wallet package.'}
                                                    </div>
                                                </div>

                                                <div className={styles.selfCustodySetupField}>
                                                    <div
                                                        className={
                                                            styles.selfCustodySetupFieldLabel
                                                        }
                                                    >
                                                        Device ID
                                                    </div>
                                                    <div
                                                        className={styles.selfCustodySetupFieldRow}
                                                    >
                                                        <code>{clientShareRecord.deviceId}</code>
                                                        <button
                                                            className={styles.clipboardButton}
                                                            onClick={() =>
                                                                handleCopySetupField(
                                                                    clientShareRecord.deviceId,
                                                                    'device-id',
                                                                )
                                                            }
                                                        >
                                                            {copiedSetupField === 'device-id' ? (
                                                                <Check size={16} />
                                                            ) : (
                                                                <Copy size={16} />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className={styles.selfCustodySetupField}>
                                                    <div
                                                        className={
                                                            styles.selfCustodySetupFieldLabel
                                                        }
                                                    >
                                                        Address
                                                    </div>
                                                    <div
                                                        className={styles.selfCustodySetupFieldRow}
                                                    >
                                                        <code>{clientShareRecord.address}</code>
                                                        <button
                                                            className={styles.clipboardButton}
                                                            onClick={() =>
                                                                handleCopySetupField(
                                                                    clientShareRecord.address,
                                                                    'client-address',
                                                                )
                                                            }
                                                        >
                                                            {copiedSetupField ===
                                                            'client-address' ? (
                                                                <Check size={16} />
                                                            ) : (
                                                                <Copy size={16} />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className={styles.selfCustodySetupStatusCard}>
                                        <div className={styles.selfCustodySetupStatusLabel}>
                                            Server Share Status
                                        </div>
                                        <div className={styles.selfCustodySetupStatusValue}>
                                            {serverShareRegistered === null
                                                ? 'Unknown'
                                                : serverShareRegistered &&
                                                  selfCustodyBrowserNeedsReinitialization
                                                ? 'Registered, but this browser needs re-setup'
                                                : serverShareRegistered === false &&
                                                  selfCustodyBrowserNeedsReinitialization
                                                ? 'Missing, and this browser needs re-setup'
                                                : serverShareRegistered
                                                ? 'Registered for this device id'
                                                : 'Missing for this device id'}
                                        </div>
                                        <div className={styles.selfCustodySetupHint}>
                                            {serverShareRegistered &&
                                            selfCustodyBrowserNeedsReinitialization
                                                ? 'The matching server share exists, but this browser cannot recover the local wallet until self-custody is re-set up here.'
                                                : serverShareRegistered === false &&
                                                  selfCustodyBrowserNeedsReinitialization
                                                ? 'This browser also needs its server share repaired, but re-setting up self-custody here is still the right first fix.'
                                                : serverShareRegistered
                                                ? 'The matching server share is registered for this browser and ready for client signing.'
                                                : clientShareRecord
                                                ? 'This browser still needs its matching server share registered before it can sign.'
                                                : 'Set up this browser to create and register a matching server share.'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentUserBsvWalletType !== BsvWalletType.PANDA && (
                            <div className={styles.withdrawContainer}>
                                <div className={styles.withdrawHeader}>
                                    <ArrowUpRight size={20} /> Send
                                </div>

                                <div className={styles.addressRow}>
                                    <div className={styles.addressLabel}>Recipient</div>
                                    <div className={styles.editorContainer}>
                                        <CommonEditor
                                            ref={addressEditorRef}
                                            placeholder="Enter address or @username"
                                            value={editorValue}
                                            onChange={onChange}
                                            type="address-input"
                                        />
                                    </div>
                                </div>

                                <div className={styles.addressRow}>
                                    <div className={styles.addressLabel}>
                                        Amount in
                                        <span className={styles.addressLabelUnit}>
                                            {unitLabel(amountUnit)}
                                        </span>
                                    </div>
                                    <div
                                        className={cn(
                                            styles.inputContainer,
                                            styles.amountInputContainer,
                                        )}
                                    >
                                        <div className={styles.amountField}>
                                            <input
                                                type="number"
                                                min="0"
                                                step={amountUnit === 'bsv' ? '0.00000001' : '1'}
                                                value={amountInput}
                                                onChange={e => setAmountInput(e.target.value)}
                                                placeholder={
                                                    amountUnit === 'bsv' ? '0.00000000' : '0'
                                                }
                                                aria-label={`Amount in ${unitLabel(amountUnit)}`}
                                                aria-describedby="wallet-send-amount-hint"
                                                aria-invalid={sendAmountHint.warning}
                                                data-testid="wallet-send-amount"
                                            />
                                            <div
                                                className={styles.amountUnitToggle}
                                                role="group"
                                                aria-label="Amount unit"
                                            >
                                                {AMOUNT_UNITS.map(unit => (
                                                    <button
                                                        key={unit}
                                                        type="button"
                                                        data-unit={unit}
                                                        className={cn(
                                                            styles.amountUnitOption,
                                                            amountUnit === unit &&
                                                                styles.amountUnitOptionActive,
                                                        )}
                                                        aria-pressed={amountUnit === unit}
                                                        onClick={() => selectAmountUnit(unit)}
                                                        data-testid={`wallet-send-unit-${unit}`}
                                                    >
                                                        {unitLabel(unit)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div
                                            className={cn(
                                                styles.amountHint,
                                                sendAmountHint.warning && styles.amountHintWarning,
                                            )}
                                            id="wallet-send-amount-hint"
                                            role={sendAmountHint.warning ? 'alert' : undefined}
                                            data-testid="wallet-send-amount-hint"
                                        >
                                            {sendAmountHint.content}
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.sendButtonWrapper}>
                                    {currentUserBsvWalletType === BsvWalletType.SHUALLET && (
                                        <a href="#" onClick={handleDownloadWallet}>
                                            <Download size={14} style={{ marginRight: 4 }} /> Backup
                                            Wallet
                                        </a>
                                    )}

                                    <button
                                        className={styles.sendBtn}
                                        disabled={!amountSats || !address || sending}
                                        onClick={handleSend}
                                    >
                                        <Send size={18} style={{ marginRight: 8 }} />{' '}
                                        {sending ? 'Sending...' : 'Send'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'stripe' && (
                    <>
                        {DEBUG && (
                            <div className={styles.stripeCard}>
                                <div className={styles.depositHeader}>
                                    <Lock size={20} /> Debug Status
                                </div>

                                {stripeStatusLoading ? (
                                    <div>Loading...</div>
                                ) : (
                                    <>
                                        <div className={styles.stripeRow}>
                                            <div className={styles.stripeKey}>Mode</div>
                                            <div className={styles.stripeValue}>
                                                {stripeStatus?.stripe?.ai_payment_mode}
                                            </div>
                                        </div>
                                        <div className={styles.stripeRow}>
                                            <div className={styles.stripeKey}>Status</div>
                                            <div className={styles.stripeValue}>
                                                {stripeStatus?.stripe?.subscription_status}
                                            </div>
                                        </div>
                                        <div className={styles.stripeRow}>
                                            <div className={styles.stripeKey}>Customer</div>
                                            <div className={styles.stripeValue}>
                                                {stripeStatus?.stripe?.customer_id}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {stripeStatus?.usd?.mode === 'credits' && stripeStatus?.usd?.credits && (
                            <WalletUsdCredits
                                credits={stripeStatus.usd.credits}
                                onChange={credits =>
                                    setStripeStatus((current: any) => ({
                                        ...(current || {}),
                                        usd: { ...(current?.usd || {}), credits },
                                    }))
                                }
                            />
                        )}

                        {stripeStatus?.usd?.mode !== 'credits' && (
                            <>
                                <div className={styles.stripeCard}>
                                    <div className={styles.depositHeader}>
                                        <Activity size={20} /> Usage
                                    </div>
                                    {!stripeStatusLoading && (
                                        <>
                                            <div className={styles.stripeRow}>
                                                <div className={styles.stripeKey}>
                                                    Outstanding (USD)
                                                </div>
                                                <div
                                                    className={styles.stripeValue}
                                                    style={{ color: 'var(--color-brand-answer)' }}
                                                >
                                                    {formatUsdValue(
                                                        (stripeStatus?.usage?.unpaid_total_cents ||
                                                            0) / 100,
                                                    )}
                                                </div>
                                            </div>
                                            <div className={styles.stripeRow}>
                                                <div className={styles.stripeKey}>Paid (USD)</div>
                                                <div
                                                    className={styles.stripeValue}
                                                    style={{ color: 'var(--color-brand-sats)' }}
                                                >
                                                    {formatUsdValue(
                                                        ((stripeStatus?.usage
                                                            ?.totals_cents_by_status?.paid ||
                                                            0) as number) / 100,
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {!(
                                    stripeStatus?.stripe?.subscription_status === 'active' ||
                                    stripeStatus?.stripe?.subscription_status === 'trialing'
                                ) && (
                                    <div
                                        className={styles.stripeCard}
                                        style={{ textAlign: 'center', padding: '40px 20px' }}
                                    >
                                        <CreditCard
                                            size={48}
                                            style={{ marginBottom: 16, opacity: 0.5 }}
                                        />
                                        <div
                                            className={styles.depositHeader}
                                            style={{ justifyContent: 'center' }}
                                        >
                                            Card billing
                                        </div>
                                        <p
                                            style={{
                                                color: 'var(--color-text-secondary)',
                                                marginBottom: 24,
                                            }}
                                        >
                                            Pay for AI generations with your card, pay as you go. No
                                            monthly fee.
                                        </p>
                                        <button
                                            className={styles.sendBtn}
                                            style={{
                                                width: 'auto',
                                                background: 'var(--color-brand-usd-green)',
                                            }}
                                            onClick={async () => {
                                                const checkout = prepareExternalCheckout()
                                                if (checkout.requiresPopup && !checkout.popup) {
                                                    alert(
                                                        'Could not open Stripe in your browser. Please try again.',
                                                    )
                                                    return
                                                }
                                                try {
                                                    setSettingUpUsdAiBilling(true)
                                                    const resp =
                                                        await api.createStripeAiBillingCheckoutSession()
                                                    if (resp?.url) {
                                                        if (
                                                            !openExternalCheckout(
                                                                resp.url,
                                                                checkout,
                                                            )
                                                        ) {
                                                            cancelExternalCheckout(checkout)
                                                            alert(
                                                                'Could not open Stripe in your browser. Please try again.',
                                                            )
                                                        }
                                                    } else {
                                                        cancelExternalCheckout(checkout)
                                                        alert(
                                                            resp?.error || 'Stripe setup required',
                                                        )
                                                    }
                                                } catch (e) {
                                                    cancelExternalCheckout(checkout)
                                                    alert('Stripe setup required')
                                                } finally {
                                                    setSettingUpUsdAiBilling(false)
                                                }
                                            }}
                                        >
                                            {settingUpUsdAiBilling
                                                ? 'Redirecting...'
                                                : 'Set up card billing'}
                                        </button>
                                    </div>
                                )}

                                <div className={styles.stripeEvents}>
                                    <div className={styles.depositHeader}>
                                        <History size={20} /> Recent Events
                                    </div>
                                    {stripeStatus?.usage?.events?.slice(0, 10).map(evt => (
                                        <div key={evt.id} className={styles.stripeEvent}>
                                            <div
                                                style={{
                                                    color: 'var(--color-text-secondary)',
                                                    fontSize: '12px',
                                                }}
                                            >
                                                {evt?.occurred_at
                                                    ? new Date(evt.occurred_at).toLocaleDateString()
                                                    : '—'}
                                            </div>
                                            <div style={{ fontWeight: 600 }}>
                                                {formatUsdValue((evt.amount_cents || 0) / 100)}
                                            </div>
                                            <div className={styles.stripeEventStatus}>
                                                {evt.status || '—'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
